import { Stack, Typography } from '@mui/material';
import { z } from 'zod';

import { CoordinatesFieldGroup } from '@/components/management/formElements/CoordinatesFieldGroup';
import { useAppForm } from '@/hooks/useAppForm';
import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';
import { I18N_DE } from '@/i18n/de';
import type { CreateStationDto } from '@/modules/api/backend';
import { StationType } from '@/modules/api/backend';
import { zStationType } from '@/modules/api/backend/zod.gen';
import { formScrollIntoViewOptions } from '@/utils/formScrollIntoViewOptions';

interface CreateStationFormProps {
  onSubmit: (values: CreateStationDto) => Promise<void>;
  setRef: (idx: number) => (ref: HTMLDivElement | null) => void;
  tabs: string[];
}

export function CreateStationForm({ setRef, tabs, onSubmit }: CreateStationFormProps) {
  const form = useAppForm({
    defaultValues: {
      name: '',
      type: StationType.TRAIN,
      rmvId: '',
      rmvNumber: null as unknown as number,
      geometry: {
        longitude: null as unknown as number,
        latitude: null as unknown as number,
      },
    },

    ...formScrollIntoViewOptions,

    onSubmit: async ({ value: { geometry, ...value } }) => {
      await onSubmit({
        ...value,
        geometry: {
          type: 'Point',
          coordinates: [geometry.longitude, geometry.latitude],
        },
      });
    },
  });

  useNavigationBlocker(form);

  return (
    <form
      id="stationForm"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Stack spacing={6}>
        <Stack spacing={2}>
          <Typography ref={setRef(0)} variant="h2">
            {tabs[0]}
          </Typography>

          <form.AppField
            name="name"
            validators={{
              onChange: z.string().nonempty(),
            }}
          >
            {(field) => <field.TextField label="Name" />}
          </form.AppField>

          <form.AppField name="type">
            {(field) => (
              <field.SelectField
                label="Stationstyp"
                options={zStationType.options.map((type) => ({ value: type, label: I18N_DE.StationType[type] }))}
              />
            )}
          </form.AppField>

          <form.AppField
            name="rmvId"
            validators={{
              onChange: z.string().nonempty(),
            }}
          >
            {(field) => <field.TextField label="Bahnhofsnummer" />}
          </form.AppField>

          <form.AppField
            name="rmvNumber"
            validators={{
              onChange: z.number(),
            }}
          >
            {(field) => <field.NumberField label="RMV-Nummer" />}
          </form.AppField>

          <CoordinatesFieldGroup disabled={false} fields="geometry" form={form} />
        </Stack>
      </Stack>
    </form>
  );
}
